import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import { getMyApplications, processPayment } from '../services/applicationService';
import { Application } from '../types';
import { CreditCardIcon, CalendarIcon, DocumentTextIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { isPaymentMockMode, logPaymentMode } from '../utils/paymentMode';

const Payment: React.FC = () => {
  const { applicationId } = useParams<{ applicationId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [application, setApplication] = useState<Application | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!user) {
      alert('로그인이 필요합니다.');
      navigate('/login');
      return;
    }
    logPaymentMode(); // 결제 모드 확인
    loadApplication();
  }, [user, applicationId]);

  const loadApplication = async () => {
    setLoading(true);
    try {
      const response = await getMyApplications('all');
      if (response.success && response.data) {
        const app = response.data.applications.find(
          (a) => a.application_id === applicationId
        );

        if (!app) {
          alert('신청 내역을 찾을 수 없습니다.');
          navigate('/mypage');
          return;
        }

        // 결제 가능 상태 검증
        if (app.status !== 'registered') {
          alert('접수 완료 상태가 아닙니다.');
          navigate('/mypage');
          return;
        }

        if (app.payment_status !== 'pending') {
          alert('결제 대기 상태가 아닙니다.');
          navigate('/mypage');
          return;
        }

        // 결제 기한 확인
        if (app.payment_deadline && new Date(app.payment_deadline) < new Date()) {
          alert('결제 기한이 만료되었습니다.\n\n고객센터로 문의해주세요.');
          navigate('/mypage');
          return;
        }

        setApplication(app);
      } else {
        alert('신청 내역을 불러오는 중 오류가 발생했습니다.');
        navigate('/mypage');
      }
    } catch (error) {
      console.error('Error loading application:', error);
      alert('신청 내역을 불러오는 중 오류가 발생했습니다.');
      navigate('/mypage');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!application) return;

    setProcessing(true);

    try {
      const isMockMode = isPaymentMockMode();

      // ===== Mock 모드: PG 없이 테스트 =====
      if (isMockMode) {
        console.log('🧪 Mock 결제 모드: PG SDK 없이 직접 결제 완료 처리');

        // Mock 결제: 즉시 process-payment 호출
        const response = await processPayment({
          application_id: application.application_id!,
          imp_uid: `mock_imp_${Date.now()}`,
          merchant_uid: application.application_number!,
          imp_success: true,
          paid_amount: application.payment_amount!,
          payment_method: 'card',
          mock_mode: true, // Mock 모드 플래그
        });

        if (response.success) {
          alert(
            '✅ 결제 완료로 처리되었습니다 (mock)\n\n' +
            `프로그램: ${application.program_name}\n` +
            `결제 금액: ${application.payment_amount?.toLocaleString()}원\n\n` +
            `마이페이지에서 결제 완료 상태를 확인할 수 있습니다.`
          );
          navigate('/mypage');
        } else {
          alert(
            '❌ 결제 처리 실패\n\n' +
            (response.error?.message || '알 수 없는 오류가 발생했습니다.')
          );
        }
        return;
      }

      // ===== Real 모드: PG SDK 연동 =====
      console.log('💳 Real 결제 모드: PG SDK 호출');

      // ===== PG SDK 연동 훅 포인트 =====
      // TODO: 아임포트 또는 토스페이먼츠 SDK 호출
      //
      // 예시 (아임포트):
      // const IMP = window.IMP;
      // IMP.init('YOUR_IMP_CODE');
      // IMP.request_pay({
      //   pg: 'html5_inicis',
      //   pay_method: 'card',
      //   merchant_uid: application.application_number,
      //   name: application.program_name,
      //   amount: application.payment_amount,
      //   buyer_email: user.email,
      //   buyer_name: application.student_name,
      // }, async (rsp) => {
      //   if (rsp.success) {
      //     // 결제 성공 → process-payment Edge Function 호출
      //     const response = await processPayment({
      //       application_id: application.application_id,
      //       imp_uid: rsp.imp_uid,
      //       merchant_uid: rsp.merchant_uid,
      //       imp_success: true,
      //       paid_amount: rsp.paid_amount,
      //       payment_method: 'card',
      //       mock_mode: false,
      //     });
      //
      //     if (response.success) {
      //       alert('결제가 완료되었습니다!');
      //       navigate('/mypage');
      //     } else {
      //       alert(response.error?.message || '결제 검증 실패');
      //     }
      //   } else {
      //     alert('결제 실패: ' + rsp.error_msg);
      //   }
      // });

      alert(
        '🚧 Real 모드: PG SDK 연동 필요\n\n' +
        '아임포트 또는 토스페이먼츠 SDK를 연동하면\n' +
        '실제 결제가 가능합니다.\n\n' +
        `결제 금액: ${application.payment_amount?.toLocaleString()}원\n` +
        `신청 번호: ${application.application_number}\n\n` +
        `테스트를 위해서는 .env 파일에서\n` +
        `VITE_PAYMENT_MODE=mock 으로 설정하세요.`
      );

      // Real 모드에서는 PG SDK 연동 전까지 마이페이지로 이동
      navigate('/mypage');
    } catch (error) {
      console.error('Payment error:', error);
      alert('결제 처리 중 오류가 발생했습니다.');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div>
        <Header />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-24">
          <div className="text-center py-12">
            <p className="text-gray-500">로딩 중...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!application) {
    return (
      <div>
        <Header />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-24">
          <div className="text-center py-12">
            <p className="text-gray-500">신청 내역을 찾을 수 없습니다.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <Header />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-12">
        {/* 뒤로가기 */}
        <button
          onClick={() => navigate('/mypage')}
          className="mb-6 inline-flex items-center text-sm font-semibold text-black hover:text-green-600 transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          마이페이지로 돌아가기
        </button>

        {/* 결제 정보 카드 */}
        <div className="bg-white rounded-lg shadow-md p-6 sm:p-8 mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">결제하기</h1>

          {/* 프로그램 정보 */}
          <div className="border-b border-gray-200 pb-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">프로그램 정보</h2>
            <div className="space-y-3">
              <div className="flex items-start">
                <DocumentTextIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500">프로그램명</p>
                  <p className="text-base font-medium text-gray-900">{application.program_name}</p>
                </div>
              </div>
              <div className="flex items-start">
                <DocumentTextIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500">수강생</p>
                  <p className="text-base font-medium text-gray-900">{application.student_name}</p>
                </div>
              </div>
              <div className="flex items-start">
                <DocumentTextIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500">신청 번호</p>
                  <p className="text-base font-medium text-gray-900">{application.application_number}</p>
                </div>
              </div>
            </div>
          </div>

          {/* 결제 정보 */}
          <div className="border-b border-gray-200 pb-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">결제 정보</h2>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">수강료</span>
                <span className="text-gray-900">{application.tuition?.toLocaleString()}원</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">재료비</span>
                <span className="text-gray-900">{application.material_fee?.toLocaleString()}원</span>
              </div>
              <div className="border-t border-gray-200 pt-3 flex justify-between items-center">
                <span className="text-lg font-semibold text-gray-900">총 결제 금액</span>
                <span className="text-2xl font-bold text-green-600">
                  {application.payment_amount?.toLocaleString()}원
                </span>
              </div>
            </div>
          </div>

          {/* 결제 기한 */}
          {application.payment_deadline && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <CalendarIcon className="h-5 w-5 text-yellow-600 mr-2" />
                <div>
                  <p className="text-sm font-medium text-yellow-800">결제 기한</p>
                  <p className="text-base font-semibold text-yellow-900">
                    {new Date(application.payment_deadline).toLocaleString('ko-KR')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* 결제 방법 선택 (향후 확장) */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">결제 방법</h2>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="flex items-center">
                <CreditCardIcon className="h-6 w-6 text-gray-400 mr-3" />
                <span className="text-gray-700">신용카드 / 체크카드</span>
              </div>
              <p className="text-sm text-gray-500 mt-2 ml-9">
                PG SDK 연동 후 다양한 결제 수단을 선택할 수 있습니다.
              </p>
            </div>
          </div>

          {/* 결제 버튼 */}
          <div className="space-y-3">
            <button
              onClick={handlePayment}
              disabled={processing}
              className="w-full bg-green-600 text-white py-4 px-6 rounded-lg font-semibold text-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {processing
                ? '처리 중...'
                : isPaymentMockMode()
                  ? `🧪 테스트 결제하기 (${application.payment_amount?.toLocaleString()}원)`
                  : `${application.payment_amount?.toLocaleString()}원 결제하기`
              }
            </button>
            <button
              onClick={() => navigate('/mypage')}
              className="w-full bg-gray-100 text-gray-700 py-3 px-6 rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              취소
            </button>
          </div>
        </div>

        {/* 안내 사항 */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">안내 사항</h3>
          <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
            <li>결제 기한 내에 결제하지 않으면 신청이 자동 취소됩니다.</li>
            <li>결제 완료 후 영수증은 마이페이지에서 확인하실 수 있습니다.</li>
            <li>환불은 고객센터로 문의해주세요.</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Payment;
